import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, ChevronRight } from "lucide-react";
import SkinPreview from "./SkinPreview";
import api from "../lib/api";

// Maps trophy-reward achievement ids → the ball-skin id they unlock.
// Stays in sync with backend `ball_skins._TROPHY_THEMES`.
const TROPHY_SKIN_MAP = {
  grandmaster: "grandmaster-s-halo",
  "thousand-breaker": "voyager-s-wake",
  "daily-streak-7": "streak-runner",
};

// Shows the user's closest in-progress trophy-reward achievement. Returns
// null when nothing qualifies (all 3 trophies already unlocked, or the
// achievements payload failed to load) so the host layout isn't disturbed.
export default function NextTrophyCard() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let cancel = false;
    Promise.all([api.get("/achievements"), api.get("/ball-skins")])
      .then(([aRes, sRes]) => {
        if (cancel) return;
        const { catalog, unlocked, stats } = aRes.data;
        const unlockedIds = new Set((unlocked || []).map((u) => u.id));
        const trophyCandidates = catalog
          .filter((a) => a.reward_skin && !unlockedIds.has(a.id))
          .map((a) => {
            const value = stats?.[a.metric] || 0;
            const progress = Math.min(1, value / a.target);
            return { ach: a, value, progress };
          })
          .sort((x, y) => y.progress - x.progress);
        const best = trophyCandidates[0];
        if (!best) {
          setState({ done: true });
          return;
        }
        const skinId = TROPHY_SKIN_MAP[best.ach.id];
        const skin = (sRes.data.skins || []).find((s) => s.id === skinId);
        setState({ best, skin });
      })
      .catch(() => !cancel && setState({ done: true }));
    return () => {
      cancel = true;
    };
  }, []);

  if (!state || state.done) return null;
  const { best, skin } = state;
  const { ach, value, progress } = best;

  return (
    <Link
      to="/achievements"
      className="block glass p-5 mb-6 border border-[#ffd700]/40 hover:border-[#ffd700] hover:shadow-[0_0_18px_rgba(255,215,0,0.25)] transition"
      data-testid="next-trophy-card"
    >
      <div className="flex items-center gap-3 sm:gap-5 flex-wrap">
        {skin && (
          <div className="flex-shrink-0">
            <SkinPreview skin={skin} size={64} aspect="square" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 font-mono-cosmic text-[9px] sm:text-[10px] tracking-[0.3em] sm:tracking-[0.35em] text-[#ffd700] uppercase mb-1">
            <Trophy className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
            Your Next Trophy
          </div>
          <div className="font-heading uppercase text-sm sm:text-xl tracking-tight text-white leading-snug">
            {ach.name}
          </div>
          <p className="text-white/60 text-xs sm:text-sm mt-0.5 mb-2 truncate">
            {ach.desc} · Unlocks <span className="text-[#ffd700]">{skin?.name || "a rare skin"}</span>
          </p>
          <div className="relative h-1.5 bg-white/10 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#ffd700]"
              style={{ width: `${progress * 100}%` }}
              data-testid="next-trophy-progress"
            />
          </div>
          <div className="flex items-center justify-between font-mono-cosmic text-[9px] sm:text-[10px] tracking-widest uppercase text-white/50 mt-1">
            <span>{value.toLocaleString()} / {ach.target.toLocaleString()}</span>
            <span className="text-[#ffd700]">+{ach.reward_score.toLocaleString()} pts</span>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-[#ffd700]/60 hidden sm:block" />
      </div>
    </Link>
  );
}
